import { useEffect, useState } from 'react';
import { PageHeader } from '../components/layout/PageHeader';
import { StatCard } from '../components/widgets/StatCard';
import { Card, CardHeader } from '../components/ui/Card';
import { Badge, Dot } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Analytics as AnalyticsApi, Listings as ListingsApi, Drafts } from '../api/client';
import {
  DollarSign,
  Eye,
  Heart,
  Package,
  ArrowUpRight,
  Sparkles,
  ListChecks,
  Wand2,
  TrendingUp,
  Activity,
  RefreshCw,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

export function Dashboard() {
  const [overview, setOverview] = useState<any>(null);
  const [listings, setListings] = useState<any[]>([]);
  const [drafts, setDrafts] = useState<any[]>([]);
  const [health, setHealth] = useState<any>(null);
  const [activity, setActivity] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [ov, ls, ds, h, act] = await Promise.all([
          AnalyticsApi.overview(),
          ListingsApi.list(),
          Drafts.list(),
          fetch('/api/health').then((r) => r.json()).then((j) => j.data ?? null).catch(() => null),
          fetch('/api/analytics/activity').then((r) => r.json()).then((j) => j.data ?? []).catch(() => []),
        ]);
        setOverview(ov);
        setListings(ls);
        setDrafts(ds);
        setHealth(h);
        setActivity(act);
      } catch (e) {
        console.warn(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div>
      <PageHeader
        kicker="// dashboard · 01"
        title="Welcome back, Pavel"
        description="A snapshot of your shop — fresh drafts, live listings, and what's trending."
        actions={
          <>
            <Button variant="secondary" leftIcon={<ListChecks size={13} />}>
              <Link to="/listings">My listings</Link>
            </Button>
            <Button variant="primary" leftIcon={<Wand2 size={13} />}>
              <Link to="/draft-studio">New draft</Link>
            </Button>
          </>
        }
      />

      {/* Hero stat row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <StatCard
          kicker="revenue · 30d"
          value={overview ? `$${Number(overview.revenue_30d ?? 0).toFixed(2)}` : '—'}
          delta={overview?.revenue_delta_pct ?? undefined}
          hint={overview?.revenue_delta_pct != null ? 'vs prior 30d' : 'lifetime'}
          icon={DollarSign}
          accent="success"
        />
        <StatCard
          kicker="views · 30d"
          value={overview ? overview.views_30d.toLocaleString() : '—'}
          hint="visits on listings"
          icon={Eye}
          accent="brand"
        />
        <StatCard
          kicker="favorites · 30d"
          value={overview ? overview.favorites_30d.toLocaleString() : '—'}
          hint="hearts added"
          icon={Heart}
          accent="warn"
        />
        <StatCard
          kicker="active listings"
          value={overview ? `${overview.listings_active}/${overview.listings_total}` : '—'}
          hint="inventory depth"
          icon={Package}
          accent="neutral"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        {/* Active listings */}
        <Card className="lg:col-span-2 p-0">
          <div className="px-5 pt-5">
            <CardHeader
              kicker="// live"
              title="Active listings"
              description="Synced from Etsy. Click to open in browser."
              actions={
                <Link to="/listings" className="text-[12px] text-etsy hover:text-etsy-600 flex items-center gap-1 font-medium">
                  See all <ArrowUpRight size={12} />
                </Link>
              }
            />
          </div>
          <div className="px-2 pb-2">
            <div className="space-y-0.5">
              {loading
                ? Array.from({ length: 3 }).map((_, i) => <div key={i} className="skeleton h-12 mx-3 my-1" />)
                : listings.slice(0, 5).map((l, i) => (
                    <motion.div
                      key={l.id}
                      initial={{ opacity: 0, x: -4 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.04 }}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-cream-100 transition"
                    >
                      <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-etsy-100 to-amber/20 flex items-center justify-center border border-cream-300 overflow-hidden shrink-0">
                        {l.thumbnail_url ? (
                          <img src={l.thumbnail_url} alt={l.title ?? ''} className="h-full w-full object-cover" loading="lazy" />
                        ) : (
                          <Package size={14} className="text-etsy" strokeWidth={1.7} />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-[13px] text-ink-800 truncate tracking-tight font-medium">{l.title ?? `Listing #${l.id}`}</div>
                        <div className="kicker mt-0.5 text-ink-400">
                          ${l.price_amount} · qty {l.quantity ?? 0}
                        </div>
                      </div>
                      <Badge tone={l.state === 'active' ? 'success' : 'neutral'}>{l.state ?? 'draft'}</Badge>
                    </motion.div>
                  ))}
            </div>
          </div>
        </Card>

        {/* Quick actions */}
        <Card noise>
          <CardHeader
            kicker="// quick"
            title="Studio actions"
            description="One-click entry points."
          />
          <div className="space-y-2">
            <QuickAction to="/draft-studio" icon={Sparkles} label="Generate draft from brief" kicker="A · image + copy" />
            <QuickAction to="/image-lab" icon={Wand2} label="Image Lab" kicker="B · variants" />
            <QuickAction to="/listings" icon={ListChecks} label="Sync from Etsy" kicker="C · manual" />
            <QuickAction to="/analytics" icon={TrendingUp} label="Open analytics" kicker="D · overview" />
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Activity — live feed from jobs table */}
        <Card className="lg:col-span-2">
          <CardHeader kicker="// activity" title="Recent activity" description="Sync runs and ad-hoc jobs." />
          <div className="space-y-2.5">
            {activity.length === 0 ? (
              <div className="text-[12px] text-ink-500 px-1 py-3">
                No jobs yet. The background scheduler runs every 15–30 minutes, or click Sync on Orders / Analytics to log a manual run.
              </div>
            ) : (
              activity.slice(0, 6).map((j) => {
                const tone = j.status === 'ok' ? 'success' : j.status === 'failed' ? 'danger' : 'brand';
                const icon = j.kind.startsWith('sync_') ? RefreshCw : Sparkles;
                const title = j.kind
                  .replace(/^sync_/, '')
                  .replace(/_manual$/, ' · manual')
                  .replace(/_/g, ' ');
                const ts = (j.finished_at ?? j.created_at) * 1000;
                const detail = j.error
                  ? j.error.slice(0, 60)
                  : j.result
                    ? (() => { try { const r = JSON.parse(j.result); return r.synced != null ? `${r.synced} items` : ''; } catch { return ''; } })()
                    : '';
                return (
                  <ActivityRow
                    key={j.id}
                    icon={icon}
                    tone={tone as 'brand' | 'success' | 'warn' | 'danger'}
                    title={title}
                    detail={detail || `kind: ${j.kind}`}
                    time={relativeTime(ts)}
                  />
                );
              })
            )}
          </div>
        </Card>

        {/* Connection status */}
        <Card noise>
          <CardHeader kicker="// system" title="Health" description="Backend, providers, DB." />
          <div className="space-y-2.5">
            <HealthRow label="Backend" value=":7777 /api/_health" tone="success" />
            <HealthRow label="SQLite" value="etsy.sqlite" tone={health?.db === 'up' ? 'success' : 'warn'} />
            <HealthRow
              label="Etsy OAuth"
              value={health?.oauth_connected ? `${health.shop_name ?? 'connected'}` : 'app pending approval'}
              tone={health?.oauth_connected ? 'success' : 'warn'}
            />
            <HealthRow
              label="Image gen"
              value={health?.image_gen_live ? `${health.image_gen_provider} · live` : `stub — wire ${health?.image_gen_provider ?? 'provider'}`}
              tone={health?.image_gen_live ? 'success' : 'warn'}
            />
            <HealthRow
              label="Copy gen"
              value={`${health?.copy_gen_provider ?? 'minimax'} M3 · live`}
              tone="success"
            />
          </div>
        </Card>
      </div>
    </div>
  );
}

function QuickAction({ to, icon: Icon, label, kicker }: { to: string; icon: any; label: string; kicker: string }) {
  return (
    <Link
      to={to}
      className="group flex items-center gap-3 p-3 rounded-xl border border-cream-300 bg-white hover:bg-cream-50 hover:border-etsy-200 transition shadow-[0_1px_2px_rgba(80,50,20,0.03)]"
    >
      <div className="h-9 w-9 rounded-lg bg-etsy-50 border border-etsy-200 flex items-center justify-center group-hover:bg-etsy group-hover:border-etsy transition">
        <Icon size={14} className="text-etsy group-hover:text-white transition" strokeWidth={1.8} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[12.5px] text-ink-800 tracking-tight font-medium">{label}</div>
        <div className="kicker mt-0.5 text-ink-400">{kicker}</div>
      </div>
      <ArrowUpRight size={13} className="text-ink-300 group-hover:text-etsy group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition" />
    </Link>
  );
}

function relativeTime(ts: number): string {
  const diffSec = Math.floor((Date.now() - ts) / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return `${Math.floor(diffSec / 86400)}d ago`;
}

function ActivityRow({ icon: Icon, tone, title, detail, time }: { icon: any; tone: 'brand'|'success'|'warn'|'danger'; title: string; detail: string; time: string }) {
  const ring = {
    brand:   'border-etsy-200 bg-etsy-50 text-etsy',
    success: 'border-sage/30 bg-sage/10 text-sage',
    warn:    'border-amber/30 bg-amber/10 text-amber',
    danger:  'border-rose-200 bg-rose-50 text-rose-500',
  }[tone];
  return (
    <div className="flex items-center gap-3">
      <div className={`h-9 w-9 rounded-xl border flex items-center justify-center ${ring}`}>
        <Icon size={13} strokeWidth={1.8} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[12.5px] text-ink-800 tracking-tight font-medium">{title}</div>
        <div className="text-[11.5px] text-ink-500">{detail}</div>
      </div>
      <div className="kicker text-ink-400">{time}</div>
    </div>
  );
}

function HealthRow({ label, value, tone }: { label: string; value: string; tone: 'success' | 'warn' | 'danger' }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1">
      <div className="flex items-center gap-2 min-w-0">
        <Dot tone={tone} />
        <span className="text-[12.5px] text-ink-700 tracking-tight">{label}</span>
      </div>
      <span className="id-chip truncate max-w-[180px]">{value}</span>
    </div>
  );
}
